import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref as dbRef, set } from 'firebase/database';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { database, storage } from '../../Firebase/firebaseconfig';
import { ArrowLeft, PackagePlus, Plus, Wand2 } from 'lucide-react';
import PopulateValuesModal from './PopulateValuesModal';
import ConfirmDialog from './ConfirmDialog';
import { compressImage } from './imageCompress';
import './admin-page.css';

function genID() {
  return 'c' + Math.floor(1000 + Math.random() * 9000);
}
function genFilename(file) {
  const ext = file.name.split('.').pop();
  return `${Date.now()}_${Math.random().toString(36).substr(2, 5)}.${ext}`;
}

export default function AddComponent() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [details, setDetails] = useState([{ key: '', value: '' }]);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [productFiles, setProductFiles] = useState([]);
  const [productPreviews, setProductPreviews] = useState([]);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPopulate, setShowPopulate] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const coverInputRef = useRef();
  const prodInputRef = useRef();

  const handleDetailChange = (i, field, val) => {
    setDetails(prev => prev.map((d, idx) => idx === i ? { ...d, [field]: val } : d));
  };
  const addDetail = () => setDetails(prev => [...prev, { key: '', value: '' }]);
  const removeDetail = (i) => setDetails(prev => prev.filter((_, idx) => idx !== i));

  const handleCover = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  };

  const handleProductImages = (e) => {
    const files = Array.from(e.target.files);
    setProductFiles(prev => [...prev, ...files]);
    setProductPreviews(prev => [...prev, ...files.map(f => URL.createObjectURL(f))]);
  };

  const removeProductImage = (i) => {
    setProductFiles(prev => prev.filter((_, idx) => idx !== i));
    setProductPreviews(prev => prev.filter((_, idx) => idx !== i));
  };

  const handlePopulate = (result) => {
    if (result.name) setName(result.name);
    if (result.specs && result.specs.length > 0) {
      setDetails(result.specs.map(s => ({ key: s.label, value: s.value })));
    }
    if (result.coverImageFile) {
      setCoverFile(result.coverImageFile);
      setCoverPreview(URL.createObjectURL(result.coverImageFile));
    }
  };

  const uploadFile = (file, path) => new Promise((resolve, reject) => {
    const ref = storageRef(storage, path);
    const task = uploadBytesResumable(ref, file);
    task.on('state_changed',
      (snap) => setProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      reject,
      () => getDownloadURL(task.snapshot.ref).then(resolve).catch(reject)
    );
  });

  const handlePreSubmit = (e) => {
    e.preventDefault();
    if (!name || !price) return setError('Name and Price are required.');
    if (!coverFile) return setError('Cover image is required.');
    setError('');
    setShowConfirm(true);
  };

  const handleSubmit = async () => {
    setShowConfirm(false);
    setError(''); setSuccess(''); setUploading(true);
    try {
      const compID = genID();
      const coverC = await compressImage(coverFile);
      const coverName = genFilename(coverC);
      await uploadFile(coverC, `components/${coverName}`);
      const imageNames = [];
      for (const file of productFiles) {
        const compressed = await compressImage(file);
        const fname = genFilename(compressed);
        await uploadFile(compressed, `components/${fname}`);
        imageNames.push(fname);
      }
      const extraDetails = {};
      details.forEach(d => {
        if (d.key.trim() && d.value.trim()) extraDetails[d.key.trim()] = d.value.trim();
      });
      await set(dbRef(database, `components/${compID}`), {
        Name: name,
        Price: Number(price),
        Cover_image: coverName,
        Images: imageNames.join(','),
        ...extraDetails,
      });
      setSuccess('Component added successfully!');
      setName(''); setPrice('');
      setDetails([{ key: '', value: '' }]);
      setCoverFile(null); setCoverPreview(null);
      setProductFiles([]); setProductPreviews([]);
      setProgress(0);
    } catch (err) {
      setError('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="ap-page">
      <div className="ap-header">
        <button className="ap-back-btn" onClick={() => navigate('/admin/dashboard')}><ArrowLeft size={18} /></button>
        <div>
          <h1 className="ap-title">Add Component</h1>
          <p className="ap-subtitle">Fill in details and upload images</p>
        </div>
      </div>

      {error && <div className="ap-error">{error}</div>}
      {success && <div className="ap-success">{success}</div>}

      <div className="ap-card">
        <form onSubmit={handlePreSubmit}>
          <div className="ap-row">
            <div className="ap-form-group">
              <label className="ap-label">Component Name</label>
              <input className="ap-input" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Kingston 8GB DDR4 RAM" />
            </div>
            <div className="ap-form-group">
              <label className="ap-label">Price (₹)</label>
              <input className="ap-input" type="number" value={price} onChange={e => setPrice(e.target.value)} placeholder="e.g. 2500" />
            </div>
          </div>

          <p className="ap-section-title">Additional Details</p>
          {details.map((d, i) => (
            <div className="ap-detail-row" key={i}>
              <input className="ap-input" placeholder="Key (e.g. Speed)" value={d.key} onChange={e => handleDetailChange(i, 'key', e.target.value)} />
              <input className="ap-input" placeholder="Value (e.g. 3200MHz)" value={d.value} onChange={e => handleDetailChange(i, 'value', e.target.value)} />
              {details.length > 1 && (
                <button type="button" className="ap-detail-row-remove" onClick={() => removeDetail(i)}>✕</button>
              )}
            </div>
          ))}
          <button type="button" className="ap-btn ap-btn-ghost" style={{ marginTop: 6, fontSize: '0.82rem', padding: '6px 14px', display:'flex', alignItems:'center', gap:5 }} onClick={addDetail}>
            <Plus size={13} /> Add Detail
          </button>

          <p className="ap-section-title">Cover Image</p>
          <div className="ap-img-upload-area" onClick={() => coverInputRef.current.click()}>
            <input ref={coverInputRef} type="file" accept="image/*" onChange={handleCover} />
            {coverPreview
              ? <img src={coverPreview} alt="cover" style={{ maxHeight: 120, borderRadius: 8 }} />
              : <><div style={{ fontSize: '1.8rem' }}>🖼️</div><div>Click to select cover image</div></>
            }
          </div>

          <p className="ap-section-title">Product Images</p>
          <div className="ap-img-upload-area" onClick={() => prodInputRef.current.click()}>
            <input ref={prodInputRef} type="file" accept="image/*" multiple onChange={handleProductImages} />
            <div style={{ fontSize: '1.8rem' }}>📷</div>
            <div>Click to add product images</div>
            <div className="ap-img-upload-hint">Multiple allowed</div>
          </div>
          {productPreviews.length > 0 && (
            <div className="ap-img-previews">
              {productPreviews.map((src, i) => (
                <div className="ap-img-thumb" key={i}>
                  <img src={src} alt={`prod-${i}`} />
                  <button type="button" className="ap-img-thumb-remove" onClick={() => removeProductImage(i)}>✕</button>
                </div>
              ))}
            </div>
          )}

          {uploading && (
            <div className="ap-progress-wrap" style={{ marginTop: 16 }}>
              <div className="ap-progress-label">Uploading… {progress}%</div>
              <div className="ap-progress-bar"><div className="ap-progress-fill" style={{ width: `${progress}%` }} /></div>
            </div>
          )}

          <div className="ap-btn-row">
            <button type="button" className="ap-btn ap-btn-ai" style={{ display:'flex', alignItems:'center', gap:6 }} onClick={() => setShowPopulate(true)}>
              <Wand2 size={15} /> AI auto fill
            </button>
            <button type="submit" className="ap-btn ap-btn-primary" disabled={uploading} style={{ display:'flex', alignItems:'center', gap:6 }}>
              <PackagePlus size={16} /> {uploading ? 'Uploading…' : 'Add Component'}
            </button>
            <button type="button" className="ap-btn ap-btn-ghost" onClick={() => navigate('/admin/dashboard')}>Cancel</button>
          </div>
        </form>
      </div>

      {showPopulate && (
        <PopulateValuesModal onClose={() => setShowPopulate(false)} onApply={handlePopulate} />
      )}

      {showConfirm && (
        <ConfirmDialog
          title="Add this component?"
          message={`"${name}" — ₹${Number(price).toLocaleString('en-IN')}`}
          confirmLabel="Yes, Add Component"
          confirmingLabel="Adding…"
          confirming={uploading}
          onConfirm={handleSubmit}
          onCancel={() => setShowConfirm(false)}
        />
      )}
    </div>
  );
}
